import {
  authApi,
  type AuthTokens,
  type SocialLoginRequest,
  type SocialProvider,
  type UserDetail,
} from '@/entities/auth/api/auth.api';
import {
  clearTokens,
  REFRESH_TOKEN_KEY,
  TOKEN_KEY,
} from '@/shared/api/http-client';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface AuthState {
  user: UserDetail | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  socialLogin: (
    provider: SocialProvider,
    body: SocialLoginRequest,
  ) => Promise<void>;
  /** 사용자 로그아웃 API가 없어 토큰을 지우는 것으로 끝낸다 */
  logout: () => Promise<void>;
  /** 새로고침 후 me 재조회 */
  refreshMe: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => {
      /**
       * 로그인 응답에는 토큰만 오므로 저장한 뒤 me로 사용자 정보를 받는다.
       * me가 실패해도 로그인은 성공으로 두고, AppLayout이 다시 조회한다.
       */
      const startSession = async (tokens: AuthTokens) => {
        localStorage.setItem(TOKEN_KEY, tokens.token);
        localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
        set({ user: null, isAuthenticated: true });
        try {
          set({ user: await authApi.me() });
        } catch {
          // AppLayout의 refreshMe가 다시 시도한다
        }
      };

      return {
        user: null,
        isAuthenticated: false,

        login: async (email, password) => {
          await startSession(await authApi.login(email, password));
        },

        socialLogin: async (provider, body) => {
          await startSession(await authApi.socialLogin(provider, body));
        },

        logout: async () => {
          clearTokens();
          set({ user: null, isAuthenticated: false });
          window.location.href = '/login';
        },

        refreshMe: async () => {
          const user = await authApi.me();
          set({ user, isAuthenticated: true });
        },
      };
    },
    {
      name: 'rudalink-web-auth',
      partialize: (s) => ({ user: s.user, isAuthenticated: s.isAuthenticated }),
    },
  ),
);

/** 라우트 가드용 — 토큰이 있어야 로그인 상태로 본다 */
export const hasAccessToken = () => !!localStorage.getItem(TOKEN_KEY);
